/**
 * ListingRow Component
 *
 * Table row for displaying listing information in the admin list.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminListing } from '../hooks';

const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const propertyTypeLabels: Record<string, string> = {
  apartment: 'Apartment',
  villa: 'Villa',
  chalet: 'Chalet',
  traditional_house: 'Traditional',
  studio: 'Studio',
  hotel_room: 'Hotel Room',
};

interface ListingRowProps {
  listing: AdminListing;
  isSelected: boolean;
  onSelect: (selected: boolean) => void;
  onAction: (action: 'view' | 'pause' | 'unpause' | 'feature' | 'unfeature' | 'approve' | 'delete') => void;
}

export const ListingRow: React.FC<ListingRowProps> = ({
  listing,
  isSelected,
  onSelect,
  onAction,
}) => {
  const [showMenu, setShowMenu] = useState(false);

  const statusColors: Record<string, string> = {
    active: 'bg-green-100 text-green-800',
    pending: 'bg-yellow-100 text-yellow-800',
    paused: 'bg-gray-100 text-gray-800',
    draft: 'bg-blue-100 text-blue-800',
    deleted: 'bg-red-100 text-red-800',
  };

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
      {/* Checkbox */}
      <td className="p-4">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSelect(e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
        />
      </td>

      {/* Listing Info */}
      <td className="p-4">
        <Link
          to={`/admin/listings/${listing.id}`}
          className="flex items-center gap-3 hover:text-primary-600"
        >
          <div className="w-14 h-14 rounded-lg bg-gray-200 overflow-hidden flex-shrink-0">
            {listing.photos[0]?.url ? (
              <img
                src={listing.photos[0].url}
                alt={listing.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                <HomeIcon className="w-6 h-6" />
              </div>
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-medium text-gray-900 truncate max-w-[200px]">
                {listing.title}
              </p>
              {listing.featured && (
                <StarIcon className="w-4 h-4 text-yellow-500 flex-shrink-0" />
              )}
            </div>
            <p className="text-sm text-gray-500">{listing.location.city}</p>
          </div>
        </Link>
      </td>

      {/* Host */}
      <td className="p-4">
        <Link
          to={`/admin/users/${listing.hostId}`}
          className="text-primary-600 hover:underline text-sm"
        >
          {listing.hostName}
        </Link>
      </td>

      {/* Type */}
      <td className="p-4 text-sm text-gray-600">
        {propertyTypeLabels[listing.propertyType] || listing.propertyType}
      </td>

      {/* Status */}
      <td className="p-4">
        <span
          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
            statusColors[listing.status]
          }`}
        >
          {listing.status}
        </span>
      </td>

      {/* Price */}
      <td className="p-4 text-sm text-gray-900">
        ${listing.pricing.nightlyRate}/night
      </td>

      {/* Stats */}
      <td className="p-4">
        <div className="text-sm">
          <p className="text-gray-900">{listing.bookingsCount} bookings</p>
          <p className="text-gray-500 text-xs">{listing.viewsCount} views</p>
        </div>
      </td>

      {/* Created */}
      <td className="p-4 text-sm text-gray-500">{formatDate(listing.createdAt)}</td>

      {/* Actions */}
      <td className="p-4">
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
          >
            <DotsIcon className="w-5 h-5" />
          </button>

          {showMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowMenu(false)}
              />
              <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-1">
                <button
                  onClick={() => {
                    window.open(`/listing/${listing.slug}`, '_blank');
                    setShowMenu(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  View as Guest
                </button>
                {listing.status === 'pending' && (
                  <button
                    onClick={() => {
                      onAction('approve');
                      setShowMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50"
                  >
                    Approve Listing
                  </button>
                )}
                {listing.status === 'active' ? (
                  <button
                    onClick={() => {
                      onAction('pause');
                      setShowMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Pause Listing
                  </button>
                ) : listing.status === 'paused' ? (
                  <button
                    onClick={() => {
                      onAction('unpause');
                      setShowMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Unpause Listing
                  </button>
                ) : null}
                {listing.featured ? (
                  <button
                    onClick={() => {
                      onAction('unfeature');
                      setShowMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Remove from Featured
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      onAction('feature');
                      setShowMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-yellow-600 hover:bg-yellow-50"
                  >
                    Feature Listing
                  </button>
                )}
                <div className="border-t border-gray-100 my-1" />
                <button
                  onClick={() => {
                    onAction('delete');
                    setShowMenu(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  Delete Listing
                </button>
              </div>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

// Icons
const HomeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const StarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
  </svg>
);

const DotsIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
  </svg>
);

export default ListingRow;
